import { UserOutlined } from '@ant-design/icons';
import { useEffect, useId, useRef, useState } from 'react';
import { authSignIn } from 'service/base';
import { CONST_SITE } from 'utils/common/const';
import { extractUserToken, persistAuthSession } from 'utils/authSession';

/**
 * Popup đăng nhập / kiểm tra thông tin — theo Figma node 90:363 (THÔNG TIN THÀNH VIÊN Hi88).
 */
export default function LoginModal({ isOpen, onClose }) {
  const titleId = useId();
  const [username, setUsername] = useState('');
  const [lastbank, setLastbank] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const bankRefs = useRef([]);

  const bankDigits = (() => {
    const raw = lastbank.replace(/\D/g, '').slice(0, 4);
    return [0, 1, 2, 3].map((i) => raw[i] ?? '');
  })();

  useEffect(() => {
    if (!isOpen) return;

    setError(null);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const setDigitAt = (index, char) => {
    const d = char.replace(/\D/g, '').slice(0, 1);
    const cur = lastbank.replace(/\D/g, '').slice(0, 4);
    const arr = cur.split('');
    while (arr.length < 4) arr.push('');
    arr[index] = d;
    setLastbank(arr.join('').replace(/\s/g, ''));
    if (d && index < 3) {
      bankRefs.current[index + 1]?.focus();
    }
  };

  const handleBankKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !bankDigits[index] && index > 0) {
      bankRefs.current[index - 1]?.focus();
    }
  };

  const handleBankPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    setLastbank(pasted);
    const focusIdx = Math.min(pasted.length, 3);
    requestAnimationFrame(() => bankRefs.current[focusIdx]?.focus());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const normalizedUsername = username.trim();
    const normalizedLastbank = lastbank.replace(/\D/g, '').slice(0, 4);

    if (!normalizedUsername) {
      setError('Vui lòng nhập tên tài khoản.');
      return;
    }

    if (normalizedLastbank.length !== 4) {
      setError('Vui lòng nhập đúng 4 số cuối tài khoản ngân hàng.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const res = await authSignIn({
        username: normalizedUsername,
        lastbank: normalizedLastbank,
        site: CONST_SITE.HI88
      });

      const ok = res?.code === 0 || res?.code === 200 || res?.success === true;
      if (!ok) {
        setError(res?.message || res?.text_mess || 'Đăng nhập thất bại.');
        return;
      }

      const userToken = extractUserToken(res);
      if (userToken) {
        persistAuthSession(userToken, normalizedUsername);
      }

      onClose();
      setUsername('');
      setLastbank('');
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.text_mess ||
        (err instanceof Error ? err.message : null) ||
        'Đăng nhập thất bại';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      aria-hidden={!isOpen}
      className={
        'fixed inset-0 z-[1000] flex items-center justify-center p-4 transition ' +
        (isOpen ? 'pointer-events-auto' : 'pointer-events-none')
      }
    >
      <button
        type="button"
        aria-label="Đóng"
        onClick={onClose}
        className={
          'absolute inset-0 bg-black/40 transition-opacity ' +
          (isOpen ? 'opacity-100' : 'opacity-0')
        }
      />

      {/* Viền gradient 4px — Figma stroke_C5CKO3 */}
      <div
        className={
          'relative rounded-[24px] p-[4px] shadow-[0_4px_8px_rgba(0,27,41,0.8)] transition-all ' +
          'bg-gradient-to-b from-[#3FB6E2] to-[#004367] ' +
          (isOpen ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.99] opacity-0')
        }
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="w-[min(347px,calc(100vw-32px))] rounded-[20px] bg-gradient-to-b from-[#E5FCFF] to-[#95C3DC] px-4 py-6"
        >
          <h2
            id={titleId}
            className="mb-4 bg-gradient-to-b from-[#1EB8F8] to-[#0D91C8] bg-clip-text text-center text-lg font-bold leading-[1.17] text-transparent"
            style={{ fontFamily: 'Roboto, sans-serif' }}
          >
            THÔNG TIN THÀNH VIÊN Hi88
          </h2>

          <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
            <div className="flex flex-col gap-2">
              <label
                htmlFor={`${titleId}-user`}
                className="text-base font-normal text-[#191919]"
                style={{ fontFamily: 'Roboto, sans-serif' }}
              >
                Tài khoản
              </label>
              <div
                className="flex h-[46px] w-full items-center gap-2 rounded-2xl border-2 border-white pl-3 pr-3 shadow-[0_2px_1.8px_#91C2D4]"
                style={{
                  background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)'
                }}
              >
                <UserOutlined className="shrink-0 text-[20px] text-[#26A9E0]" />
                <input
                  id={`${titleId}-user`}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="min-w-0 flex-1 border-0 bg-transparent text-base text-[#191919] outline-none placeholder:text-[#868585]"
                  style={{ fontFamily: 'Roboto, sans-serif' }}
                  placeholder="Vui lòng nhập tên tài khoản"
                  autoComplete="username"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span
                className="text-base font-normal text-[#191919]"
                style={{ fontFamily: 'Roboto, sans-serif' }}
              >
                4 số cuối tài khoản ngân hàng
              </span>
              <div className="flex gap-2" onPaste={handleBankPaste}>
                {[0, 1, 2, 3].map((i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      bankRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={1}
                    value={bankDigits[i]}
                    onChange={(e) => setDigitAt(i, e.target.value)}
                    onKeyDown={(e) => handleBankKeyDown(i, e)}
                    disabled={isSubmitting}
                    className={
                      'flex h-[45px] min-w-0 flex-1 border-2 border-white text-center text-base font-normal outline-none shadow-[0_2px_1.8px_#91C2D4] ' +
                      (i === 3 ? 'rounded-[30px] text-[#656565]' : 'rounded-2xl text-[#656565]')
                    }
                    style={{
                      fontFamily: 'Roboto, sans-serif',
                      background:
                        i === 3
                          ? 'linear-gradient(180deg, #F4FFF7 0%, #FFFFFF 53%, #F4FFF7 100%)'
                          : 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)'
                    }}
                    placeholder="-"
                    aria-label={`Số thứ ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            {error ? (
              <div className="rounded-xl border border-red-200/80 bg-red-50/95 px-3 py-2 text-sm font-medium text-red-700">
                {error}
              </div>
            ) : null}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex h-[49px] flex-1 items-center justify-center rounded-lg border-2 border-[#9FC6D8] text-lg font-bold uppercase tracking-wide text-[#191919] transition hover:brightness-[1.02] disabled:opacity-60"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  background: 'linear-gradient(180deg, #E7F8FE 0%, #B4DFEF 100%)'
                }}
              >
                HỦY
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={
                  'flex h-[49px] flex-1 items-center justify-center rounded-lg border-2 border-white/35 text-lg font-bold uppercase tracking-wide text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] transition ' +
                  'bg-gradient-to-b from-[#1EB8F8] to-[#0D91C8] hover:brightness-[1.03] active:translate-y-px disabled:opacity-70'
                }
                style={{ fontFamily: 'Roboto, sans-serif' }}
              >
                {isSubmitting ? 'ĐANG XỬ LÝ...' : 'XÁC NHẬN'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
